/**
 * Raketech Underpaid Report
 * Shows all underpaid accounts with 5% deduction on Synced/Manual Check,
 * source attribution, and per-month invoice drilldown.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  FileText,
  Loader2,
  AlertTriangle,
  Scale,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ArrowDown,
  ArrowUp,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface InvoiceMonth {
  month: string;
  ftds: number;
  revenue: number;
}

interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  partner: string;
  offer: string;
  geo: string;
  currency: string;
  details: string;

  invoiceFTDs?: number;
  invoiceRevenue?: number;
  reconciliationEarnings?: number;
  reconciliationEarningsOriginal?: number;
  reconciliationRows?: number;

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;

  partnerCommission?: number;
  partnerCommissionOriginal?: number;

  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;

  revenueDifference?: number;
  percentageDifference?: number;
  exchangeRate?: number;

  missingInInvoice?: boolean;
  missingInReconciliation?: boolean;

  invoiceMonthly?: InvoiceMonth[];
  invoiceEntries?: { label: string; ftds: number; revenue: number; monthly: InvoiceMonth[] }[];

  reconMonthly?: { month: string; earnings: number; ftds: number }[];
  syncedMonthly?: { month: string; commission: number; ftds: number }[];
}

interface CompareData {
  period: string;
  issues: ComparisonIssue[];
}

// ---------------------------------------------------------------------------
// Report row with computed fields
// ---------------------------------------------------------------------------
interface ReportRow {
  issue: ComparisonIssue;
  invoiced: number;
  incomeAmount: number;
  incomeSource: string;
  incomeOriginal: number;
  incomeOriginalCurrency?: string;
  incomeOriginalAmount?: number;
  underpaid: number;
  underpaidPct: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginal = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtMonth = (m: string) => {
  if (!m || m === 'unknown') return m;
  // Handle "YYYY-MM" format
  if (/^\d{4}-\d{2}$/.test(m)) {
    const [y, mo] = m.split('-');
    return `${MONTH_SHORT[parseInt(mo, 10) - 1] || mo} ${y}`;
  }
  // Already in "Month YYYY" format — return as-is
  return m;
};

const monthToSortKey = (m: string): string => {
  if (/^\d{4}-\d{2}$/.test(m)) return m;
  // DD/MM/YYYY → YYYY-MM
  const slashMatch = m.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[2]}-${slashMatch[1]}`;
  // "Month YYYY" → "YYYY-MM"
  const parts = m.trim().split(/\s+/);
  if (parts.length === 2) {
    const idx = MONTH_NAMES.indexOf(parts[0]);
    if (idx >= 0) return `${parts[1]}-${String(idx + 1).padStart(2, '0')}`;
  }
  return m;
};

const sortMonthly = (months: InvoiceMonth[]): InvoiceMonth[] =>
  [...months].sort((a, b) => monthToSortKey(a.month).localeCompare(monthToSortKey(b.month)));

type SortField = 'partner' | 'accountId' | 'invoiced' | 'income' | 'underpaid' | 'pct';
type SortDir = 'asc' | 'desc';

function computeReportRow(iss: ComparisonIssue): ReportRow | null {
  const invoiced = iss.invoiceRevenue || 0;
  if (invoiced <= 0) return null;

  const recon = iss.reconciliationEarnings || 0;
  const syncedRaw = iss.adwstatsCommission || 0;
  const synced95 = syncedRaw * 0.95;
  const manualRaw = iss.manualCheckAmount || 0;
  const manual95 = manualRaw * 0.95;

  let incomeAmount = recon;
  let incomeSource = 'Reconciliation';
  let incomeOriginal = recon;
  let incomeOriginalCurrency: string | undefined;
  let incomeOriginalAmount: number | undefined;

  if (recon > 0 && iss.reconciliationEarningsOriginal && iss.currency && iss.currency !== 'EUR') {
    incomeOriginalCurrency = iss.currency;
    incomeOriginalAmount = iss.reconciliationEarningsOriginal;
  }

  if (synced95 > incomeAmount) {
    incomeAmount = synced95;
    incomeSource = 'Synced From Operator (-5%)';
    incomeOriginal = syncedRaw;
    incomeOriginalCurrency = iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' ? iss.adwstatsCurrency : undefined;
    incomeOriginalAmount = incomeOriginalCurrency && iss.adwstatsCommissionOriginal ? iss.adwstatsCommissionOriginal : undefined;
  }
  if (manual95 > incomeAmount) {
    incomeAmount = manual95;
    incomeSource = 'Manually Checked Backend (-5%)';
    incomeOriginal = manualRaw;
    if (iss.partnerCommissionOriginal && iss.currency && iss.currency !== 'EUR') {
      incomeOriginalCurrency = iss.currency;
      incomeOriginalAmount = iss.partnerCommissionOriginal;
    } else {
      incomeOriginalCurrency = undefined;
      incomeOriginalAmount = undefined;
    }
  }

  if (incomeAmount <= 0) return null;

  const underpaid = invoiced - incomeAmount;
  if (underpaid >= -1) return null;

  const underpaidPct = incomeAmount > 0 ? (underpaid / incomeAmount) * 100 : 0;

  return { issue: iss, invoiced, incomeAmount, incomeSource, incomeOriginal, incomeOriginalCurrency, incomeOriginalAmount, underpaid, underpaidPct };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechReport: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('underpaid');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed');
      if (json.data.empty) { setError(json.data.message); setCompareData(null); }
      else setCompareData(json.data);
    } catch (err: any) { setError(err.message); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reportRows = useMemo(() => {
    if (!compareData) return [];
    const rows: ReportRow[] = [];
    for (const iss of compareData.issues) {
      const row = computeReportRow(iss);
      if (row) rows.push(row);
    }
    return rows;
  }, [compareData]);

  const filteredRows = useMemo(() => {
    let items = reportRows;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(r =>
        r.issue.partner?.toLowerCase().includes(q) ||
        r.issue.brand?.toLowerCase().includes(q) ||
        r.issue.accountId?.toLowerCase().includes(q) ||
        r.issue.username?.toLowerCase().includes(q) ||
        r.issue.geo?.toLowerCase().includes(q) ||
        r.issue.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }
    const getVal = (r: ReportRow): number | string => {
      switch (sortField) {
        case 'partner': return (r.issue.partner || '').toLowerCase();
        case 'accountId': return r.issue.accountId || '';
        case 'invoiced': return r.invoiced;
        case 'income': return r.incomeAmount;
        case 'underpaid': return r.underpaid;
        case 'pct': return r.underpaidPct;
        default: return 0;
      }
    };
    return [...items].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [reportRows, searchTerm, sortField, sortDir]);

  const totals = useMemo(() => {
    let invoiced = 0, income = 0, underpaid = 0;
    for (const r of reportRows) {
      invoiced += r.invoiced;
      income += r.incomeAmount;
      underpaid += r.underpaid;
    }
    return { invoiced, income, underpaid, count: reportRows.length };
  }, [reportRows]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'partner' || field === 'accountId' ? 'asc' : 'asc'); }
  };

  const SortHeader: React.FC<{ field: SortField; label: string; align?: string }> = ({ field, label, align }) => (
    <th
      className={`px-3 py-2.5 font-medium cursor-pointer hover:text-zinc-300 select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'} text-zinc-500`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        )}
      </span>
    </th>
  );

  const sourceColor = (src: string) => {
    if (src.includes('Synced')) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (src.includes('Manual')) return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20';
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  };

  const sourceShort = (src: string) => {
    if (src.includes('Synced')) return 'Synced -5%';
    if (src.includes('Manual')) return 'Manual -5%';
    return 'Recon';
  };

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <FileText className="h-7 w-7 text-red-400" />
              Raketech Underpaid Report
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              All underpaid accounts with verified income sources (Mar 2023 - Nov 2024)
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[hsl(225,15%,15%)] hover:bg-[hsl(225,15%,20%)] rounded-lg border border-[hsl(225,15%,22%)] transition-colors disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-red-400 mr-3" />
          <span className="text-zinc-400">Loading report data...</span>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Data Available</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {/* Main content */}
      {compareData && !isLoading && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Underpaid Accounts" value={String(totals.count)} icon={<AlertTriangle className="h-4 w-4" />} color="red" />
            <SummaryCard label="Total Invoiced" value={fmtCurrency(totals.invoiced)} icon={<FileText className="h-4 w-4" />} color="zinc" />
            <SummaryCard label="Total Income (Best Source)" value={fmtCurrency(totals.income)} icon={<Scale className="h-4 w-4" />} color="emerald" />
            <SummaryCard label="Total Underpaid" value={fmtCurrency(totals.underpaid)} icon={<AlertTriangle className="h-4 w-4" />} color="red" />
          </div>

          {/* Info banner */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4 text-xs text-zinc-400 space-y-1">
            <p><strong className="text-zinc-300">Income calculation:</strong> MAX(Reconciliation, Synced From Operator x 0.95, Manually Checked Backend x 0.95)</p>
            <p><strong className="text-zinc-300">5% deduction:</strong> Applied to "Synced From Operator" and "Manually Checked Backend" amounts to account for Raketech's share. Reconciliation data is used as-is.</p>
            <p><strong className="text-zinc-300">Underpaid:</strong> Invoiced amount minus the best available income source. Click any row to see monthly invoice breakdown.</p>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search partner, brand, account..."
              className="px-3 py-1.5 w-72 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-red-500/50"
            />
            <span className="text-xs text-zinc-500">{filteredRows.length} accounts</span>
          </div>

          {/* Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)] sticky top-0">
                  <tr>
                    <th className="w-8 px-2 py-2.5"></th>
                    <SortHeader field="partner" label="Partner / Brand" />
                    <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">GEO</th>
                    <SortHeader field="accountId" label="Account" />
                    <SortHeader field="invoiced" label="Invoiced" align="right" />
                    <SortHeader field="income" label="Income" align="right" />
                    <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Source</th>
                    <SortHeader field="underpaid" label="Underpaid" align="right" />
                    <SortHeader field="pct" label="%" align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                  {filteredRows.map((row, idx) => {
                    const key = `${row.issue.accountId}-${row.issue.offer}-${idx}`;
                    const isExpanded = expandedRows.has(key);
                    const entries = row.issue.invoiceEntries;
                    const monthly = sortMonthly(row.issue.invoiceMonthly || []);
                    const hasEntries = entries && entries.length > 0;
                    const hasReconOrSynced = (row.issue.reconMonthly?.length || 0) > 0 || (row.issue.syncedMonthly?.length || 0) > 0;
                    const hasDrilldown = hasEntries || monthly.length > 0 || hasReconOrSynced;

                    return (
                      <React.Fragment key={key}>
                        <tr
                          className="hover:bg-[hsl(225,15%,12%)] cursor-pointer"
                          onClick={() => toggleRow(key)}
                        >
                          <td className="px-2 py-2 text-zinc-500">
                            {hasDrilldown ? (
                              isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
                            ) : null}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-white font-medium">{row.issue.partner}</div>
                            <div className="text-zinc-500 text-[10px]">{row.issue.brand}{row.issue.offer ? ` — ${row.issue.offer}` : ''}</div>
                          </td>
                          <td className="px-3 py-2 text-purple-400 font-medium">{row.issue.geo}</td>
                          <td className="px-3 py-2">
                            <div className="text-zinc-300 tabular-nums">{row.issue.accountId}</div>
                            <div className="text-zinc-500 text-[10px] truncate max-w-[140px]">{row.issue.username}</div>
                            {row.issue.adwstatsAccountName && (
                              <div className="text-emerald-500/70 text-[10px] truncate max-w-[140px]" title={row.issue.adwstatsAccountName}>
                                ↔ {row.issue.adwstatsAccountName}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{fmtCurrency(row.invoiced)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div className="text-emerald-400">{fmtCurrency(row.incomeAmount)}</div>
                            {row.incomeSource !== 'Reconciliation' && (
                              <div className="text-[9px] text-zinc-500">was {fmtCurrency(row.incomeOriginal)}</div>
                            )}
                            {row.incomeOriginalCurrency && row.incomeOriginalAmount != null && (
                              <div className="text-[9px] text-zinc-500">
                                {fmtOriginal(row.incomeOriginalAmount, row.incomeOriginalCurrency)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceColor(row.incomeSource)}`}>
                              {sourceShort(row.incomeSource)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-red-400 tabular-nums font-medium">{fmtCurrency(row.underpaid)}</td>
                          <td className="px-3 py-2 text-right text-red-400 tabular-nums">{row.underpaidPct.toFixed(1)}%</td>
                        </tr>

                        {/* Drilldown */}
                        {isExpanded && hasDrilldown && (() => {
                          const reconM = row.issue.reconMonthly || [];
                          const syncedM = row.issue.syncedMonthly || [];
                          const hasComparison = reconM.length > 0 || syncedM.length > 0;

                          // Aggregate invoice monthly from all entries (not just invoiceMonthly which may only have the first offer)
                          const invoiceByMonth = new Map<string, InvoiceMonth>();
                          if (hasEntries && entries) {
                            for (const entry of entries) {
                              for (const m of (entry.monthly || [])) {
                                const key = monthToSortKey(m.month);
                                const existing = invoiceByMonth.get(key);
                                if (existing) {
                                  existing.ftds += m.ftds;
                                  existing.revenue += m.revenue;
                                } else {
                                  invoiceByMonth.set(key, { month: m.month, ftds: m.ftds, revenue: m.revenue });
                                }
                              }
                            }
                          } else {
                            for (const m of monthly) {
                              invoiceByMonth.set(monthToSortKey(m.month), m);
                            }
                          }

                          // Build unified month set for comparison
                          const allMonthsSet = new Set<string>();
                          invoiceByMonth.forEach((_, k) => allMonthsSet.add(k));
                          reconM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                          syncedM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                          const allMonths = [...allMonthsSet].sort();

                          const reconByMonth = new Map(reconM.map(m => [monthToSortKey(m.month), m]));
                          const syncedByMonth = new Map(syncedM.map(m => [monthToSortKey(m.month), m]));

                          return (
                            <tr>
                              <td colSpan={9} className="bg-[hsl(225,15%,8%)] px-6 py-3">
                                <div className="space-y-4">
                                  {/* Invoice entries (per-offer breakdown) */}
                                  {hasEntries && (
                                    <>
                                      <div className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider">
                                        Invoice Breakdown — {row.issue.partner} / {row.issue.brand} ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
                                      </div>
                                      {entries.map((entry, ei) => {
                                        const sorted = sortMonthly(entry.monthly || []);
                                        return (
                                          <div key={ei} className="border border-[hsl(225,15%,15%)] rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-[11px] text-amber-400 font-medium truncate max-w-[500px]" title={entry.label}>{entry.label}</span>
                                              <span className="text-[11px] text-zinc-300 tabular-nums font-medium">{fmtCurrency(entry.revenue)}</span>
                                            </div>
                                            {sorted.length > 0 && (
                                              <table className="w-full max-w-xl text-xs">
                                                <thead>
                                                  <tr className="text-zinc-500">
                                                    <th className="text-left py-1 px-2 font-medium">Month</th>
                                                    <th className="text-right py-1 px-2 font-medium">FTDs</th>
                                                    <th className="text-right py-1 px-2 font-medium">Revenue (EUR)</th>
                                                  </tr>
                                                </thead>
                                                <tbody className="divide-y divide-[hsl(225,15%,11%)]">
                                                  {sorted.map(m => (
                                                    <tr key={m.month} className="text-zinc-300">
                                                      <td className="py-1 px-2">{fmtMonth(m.month)}</td>
                                                      <td className="py-1 px-2 text-right tabular-nums">{m.ftds.toLocaleString()}</td>
                                                      <td className="py-1 px-2 text-right tabular-nums">{fmtCurrency(m.revenue)}</td>
                                                    </tr>
                                                  ))}
                                                  <tr className="text-white font-medium border-t border-zinc-600">
                                                    <td className="py-1.5 px-2">Subtotal</td>
                                                    <td className="py-1.5 px-2 text-right tabular-nums">{sorted.reduce((s, m) => s + m.ftds, 0).toLocaleString()}</td>
                                                    <td className="py-1.5 px-2 text-right tabular-nums">{fmtCurrency(sorted.reduce((s, m) => s + m.revenue, 0))}</td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            )}
                                          </div>
                                        );
                                      })}
                                      <div className="flex items-center justify-between border-t border-zinc-600 pt-2">
                                        <span className="text-xs text-white font-bold">Total Invoiced</span>
                                        <span className="text-xs text-white font-bold tabular-nums">{fmtCurrency(entries.reduce((s, e) => s + e.revenue, 0))}</span>
                                      </div>
                                    </>
                                  )}

                                  {/* Monthly comparison table (Invoice vs Recon vs Synced) */}
                                  {hasComparison && allMonths.length > 0 && (
                                    <div className="border border-[hsl(225,15%,15%)] rounded-lg p-3">
                                      <div className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider mb-2">
                                        Monthly Comparison — Invoice vs Reconciliation{syncedM.length > 0 ? ' vs Synced' : ''}
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead>
                                            <tr className="text-zinc-500">
                                              <th className="text-left py-1 px-2 font-medium">Month</th>
                                              <th className="text-right py-1 px-2 font-medium text-zinc-400">Invoice</th>
                                              <th className="text-right py-1 px-2 font-medium text-amber-400/70">Recon</th>
                                              {syncedM.length > 0 && (
                                                <th className="text-right py-1 px-2 font-medium text-emerald-400/70">Synced</th>
                                              )}
                                              <th className="text-right py-1 px-2 font-medium text-red-400/70">Diff</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-[hsl(225,15%,11%)]">
                                            {allMonths.map(m => {
                                              const inv = invoiceByMonth.get(m);
                                              const rec = reconByMonth.get(m);
                                              const syn = syncedByMonth.get(m);
                                              const invAmt = inv?.revenue || 0;
                                              const bestIncome = Math.max(rec?.earnings || 0, (syn?.commission || 0) * 0.95);
                                              const diff = invAmt > 0 && bestIncome > 0 ? invAmt - bestIncome : 0;
                                              const diffColor = Math.abs(diff) < 1 ? 'text-zinc-600' : diff < 0 ? 'text-emerald-400' : 'text-red-400';

                                              return (
                                                <tr key={m} className="text-zinc-300">
                                                  <td className="py-1 px-2">{fmtMonth(m)}</td>
                                                  <td className="py-1 px-2 text-right tabular-nums">
                                                    {invAmt > 0 ? fmtCurrency(invAmt) : <span className="text-zinc-600">—</span>}
                                                  </td>
                                                  <td className="py-1 px-2 text-right tabular-nums text-amber-400">
                                                    {rec ? fmtCurrency(rec.earnings) : <span className="text-zinc-600">—</span>}
                                                  </td>
                                                  {syncedM.length > 0 && (
                                                    <td className="py-1 px-2 text-right tabular-nums text-emerald-400">
                                                      {syn ? fmtCurrency(syn.commission) : <span className="text-zinc-600">—</span>}
                                                    </td>
                                                  )}
                                                  <td className={`py-1 px-2 text-right tabular-nums ${diffColor}`}>
                                                    {Math.abs(diff) > 0.01 ? fmtCurrency(diff) : <span className="text-zinc-600">—</span>}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                            {(() => {
                                              const invTotal = [...invoiceByMonth.values()].reduce((s, m) => s + m.revenue, 0);
                                              const reconTotal = reconM.reduce((s, m) => s + m.earnings, 0);
                                              const syncedTotal = syncedM.reduce((s, m) => s + m.commission, 0);
                                              return (
                                                <tr className="text-white font-medium border-t border-zinc-600">
                                                  <td className="py-1.5 px-2">Total</td>
                                                  <td className="py-1.5 px-2 text-right tabular-nums">{fmtCurrency(invTotal)}</td>
                                                  <td className="py-1.5 px-2 text-right tabular-nums text-amber-400">
                                                    {reconM.length > 0 ? fmtCurrency(reconTotal) : '—'}
                                                  </td>
                                                  {syncedM.length > 0 && (
                                                    <td className="py-1.5 px-2 text-right tabular-nums text-emerald-400">
                                                      {fmtCurrency(syncedTotal)}
                                                    </td>
                                                  )}
                                                  <td className="py-1.5 px-2 text-right tabular-nums text-red-400">
                                                    {fmtCurrency(invTotal - Math.max(reconTotal, syncedTotal * 0.95))}
                                                  </td>
                                                </tr>
                                              );
                                            })()}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Fallback: simple invoice monthly if no entries and no comparison */}
                                  {!hasEntries && !hasComparison && monthly.length > 0 && (
                                    <>
                                      <div className="text-[10px] text-zinc-400 mb-2 font-medium uppercase tracking-wider">Monthly Invoice Breakdown — {row.issue.partner} / {row.issue.brand}</div>
                                      <table className="w-full max-w-xl text-xs">
                                        <thead>
                                          <tr className="text-zinc-500">
                                            <th className="text-left py-1 px-2 font-medium">Month</th>
                                            <th className="text-right py-1 px-2 font-medium">FTDs</th>
                                            <th className="text-right py-1 px-2 font-medium">Revenue (EUR)</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[hsl(225,15%,11%)]">
                                          {monthly.map(m => (
                                            <tr key={m.month} className="text-zinc-300">
                                              <td className="py-1 px-2">{fmtMonth(m.month)}</td>
                                              <td className="py-1 px-2 text-right tabular-nums">{m.ftds.toLocaleString()}</td>
                                              <td className="py-1 px-2 text-right tabular-nums">{fmtCurrency(m.revenue)}</td>
                                            </tr>
                                          ))}
                                          <tr className="text-white font-medium border-t border-zinc-600">
                                            <td className="py-1.5 px-2">Total</td>
                                            <td className="py-1.5 px-2 text-right tabular-nums">{monthly.reduce((s, m) => s + m.ftds, 0).toLocaleString()}</td>
                                            <td className="py-1.5 px-2 text-right tabular-nums">{fmtCurrency(monthly.reduce((s, m) => s + m.revenue, 0))}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredRows.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">No underpaid accounts found</div>
            )}
          </div>

          {/* Totals footer */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-red-500/20 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-400">
                <strong className="text-white">{totals.count}</strong> underpaid accounts across all partners
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-zinc-500">Invoiced: </span>
                  <span className="text-white tabular-nums font-medium">{fmtCurrency(totals.invoiced)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Income: </span>
                  <span className="text-emerald-400 tabular-nums font-medium">{fmtCurrency(totals.income)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Underpaid: </span>
                  <span className="text-red-400 tabular-nums font-bold">{fmtCurrency(totals.underpaid)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = colorMap[color] || colorMap.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-4 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg sm:text-xl font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

export default RaketechReport;
